import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Eye, ArrowLeft, Lightning, TrendUp, Clock, ArrowUp, ArrowDown, Lock, X, ChartLine, Note, Shield, CaretRight } from 'phosphor-react';
import GeniLoader from '../components/GeniLoader';
import ConversionCTA from '../components/ConversionCTA';
import { publicApi } from '../lib/api';
import { format } from 'date-fns';

// GENI Brain logo fallback for avatars
const GENI_BRAIN_FALLBACK = "https://customer-assets.emergentagent.com/job_chart-analyzer-107/artifacts/gtg4vc6a_purebrain.png";

// Official GENI horizontal logo (Brain + Text)
const LOGO_HORIZONTAL = "https://customer-assets.emergentagent.com/job_chart-analyzer-107/artifacts/r9xkm2vj_cerebro%20%2B%20genilateral.png";

/**
 * SharedPositionsPage
 * 
 * PUBLIC page to view a trader's open positions.
 * 
 * This is COMPLETELY SEPARATE from the Public Profile:
 * - Does NOT show historical performance (Win Rate, Avg RR, Profit Factor)
 * - Does NOT show Analytics, Trades, About sections
 * - Does NOT require public_profile_enabled
 * - ONLY requires positions_sharing_enabled
 * 
 * Features:
 * - Shows trader avatar and username
 * - Shows all open positions in read-only mode
 * - Shows Active/Pending counts
 * - Clickable positions to view details (read-only)
 * - Shows Hedge/Partial badges when applicable
 * - No action buttons (Close, Cancel, Edit, etc.)
 */
export default function SharedPositionsPage() {
  const { username } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [profileInfo, setProfileInfo] = useState(null);
  const [positions, setPositions] = useState([]);
  const [stats, setStats] = useState({ active_count: 0, pending_count: 0, total_count: 0 });
  
  // Detail drawer state
  const [selectedPosition, setSelectedPosition] = useState(null);

  useEffect(() => {
    async function fetchSharedPositions() {
      if (!username) {
        setError('Invalid username');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        const response = await publicApi.get(`/public/shared-positions/${encodeURIComponent(username)}`);
        
        setProfileInfo(response.data.profile_info);
        setPositions(response.data.positions || []);
        setStats(response.data.stats || { active_count: 0, pending_count: 0, total_count: 0 });
      } catch (err) {
        console.error('Error fetching shared positions:', err);
        if (err.response?.status === 403) {
          setError('sharing_disabled');
        } else if (err.response?.status === 404) {
          setError('not_found');
        } else {
          setError('fetch_error');
        }
      } finally {
        setLoading(false);
      }
    }

    fetchSharedPositions();
  }, [username]);

  // Get trade type badge
  const getTradeTypeBadge = (tradeType) => {
    switch (tradeType) {
      case 'scalp':
        return (
          <span className="flex items-center gap-1 px-2 py-0.5 bg-yellow-500/10 border border-yellow-500/30 rounded text-xs text-yellow-400">
            <Lightning size={12} weight="fill" />
            SCALP
          </span>
        );
      case 'day_trade':
        return (
          <span className="flex items-center gap-1 px-2 py-0.5 bg-[#00E676]/10 border border-[#00E676]/30 rounded text-xs text-[#00E676]">
            <TrendUp size={12} weight="fill" />
            DAY
          </span>
        );
      case 'swing_trade':
        return (
          <span className="flex items-center gap-1 px-2 py-0.5 bg-purple-500/10 border border-purple-500/30 rounded text-xs text-purple-400">
            <Clock size={12} weight="fill" />
            SWING
          </span>
        );
      default:
        return null;
    }
  };

  // Format price
  const formatPrice = (price) => {
    if (!price && price !== 0) return '—';
    return typeof price === 'number' ? `$${price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 6 })}` : price;
  };

  // Format date short
  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Format date with time
  const formatDateTime = (dateString) => {
    if (!dateString) return '—';
    try {
      const date = new Date(dateString);
      return format(date, "MMM d, yyyy HH:mm");
    } catch {
      return '—';
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <GeniLoader size="lg" />
      </div>
    );
  }

  // Sharing disabled state
  if (error === 'sharing_disabled') {
    return (
      <div className="min-h-screen bg-[#0A0A0A]">
        {/* Simple header */}
        <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-white/10">
          <div className="max-w-[1600px] mx-auto px-4 h-14 flex items-center">
            <Link to="/" className="flex items-center">
              <img src={LOGO_HORIZONTAL} alt="GENI" className="h-6" />
            </Link>
          </div>
        </nav>
        
        <div className="pt-20 flex flex-col items-center justify-center min-h-[60vh] px-4">
          <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mb-6">
            <Lock size={32} className="text-white/40" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Position Sharing Disabled</h1>
          <p className="text-white/60 text-center max-w-md">
            This trader has disabled position sharing. The link may work again in the future.
          </p>
        </div>
        
        <ConversionCTA />
      </div>
    );
  }

  // Not found state
  if (error === 'not_found') {
    return (
      <div className="min-h-screen bg-[#0A0A0A]">
        <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-white/10">
          <div className="max-w-[1600px] mx-auto px-4 h-14 flex items-center">
            <Link to="/" className="flex items-center">
              <img src={LOGO_HORIZONTAL} alt="GENI" className="h-6" />
            </Link>
          </div>
        </nav>
        
        <div className="pt-20 flex flex-col items-center justify-center min-h-[60vh] px-4">
          <h1 className="text-2xl font-bold text-white mb-2">User Not Found</h1>
          <p className="text-white/60 text-center">This trader profile does not exist.</p>
        </div>
        
        <ConversionCTA />
      </div>
    );
  }

  // Generic error state
  if (error) {
    return (
      <div className="min-h-screen bg-[#0A0A0A]">
        <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-white/10">
          <div className="max-w-[1600px] mx-auto px-4 h-14 flex items-center">
            <Link to="/" className="flex items-center">
              <img src={LOGO_HORIZONTAL} alt="GENI" className="h-6" />
            </Link>
          </div>
        </nav>
        
        <div className="pt-20 flex flex-col items-center justify-center min-h-[60vh] px-4">
          <h1 className="text-2xl font-bold text-white mb-2">Something Went Wrong</h1>
          <p className="text-white/60 text-center">Unable to load positions. Please try again later.</p>
        </div>
        
        <ConversionCTA />
      </div>
    );
  }

  // Success state - show positions
  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Simple navigation - GENI only */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-[1600px] mx-auto px-4 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img src={LOGO_HORIZONTAL} alt="GENI" className="h-6" />
          </Link>
          <div className="flex items-center gap-1.5 text-white/40 text-xs">
            <Eye size={14} weight="bold" />
            <span>READ ONLY</span>
          </div>
        </div>
      </nav>

      {/* Main content */}
      <div className="pt-20 pb-24 px-4 md:px-8 max-w-[1600px] mx-auto">
        {/* Trader identity */}
        <div className="flex items-center gap-4 mb-8">
          <div className="w-14 h-14 rounded-full overflow-hidden border border-white/20 bg-[#121212] flex-shrink-0">
            <img
              src={profileInfo?.avatar_url || GENI_BRAIN_FALLBACK}
              alt={`${profileInfo?.username || 'Trader'}'s avatar`}
              className="w-full h-full object-cover"
              onError={(e) => { e.target.src = GENI_BRAIN_FALLBACK; }}
            />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">{profileInfo?.username || 'Trader'}</h1>
            <div className="flex items-center gap-1.5 text-white/40 text-sm">
              <Eye size={14} />
              <span>Shared Open Positions</span>
            </div>
          </div>
        </div>

        {/* Stats bar */}
        <div className="flex items-center gap-6 mb-6 py-3 px-4 bg-[#121212] border border-white/10 rounded-sm">
          <div className="flex items-center gap-2">
            <span className="text-[#00E676] font-mono font-bold text-lg">{stats.active_count}</span>
            <span className="text-white/40 text-sm">ACTIVE</span>
          </div>
          <div className="w-px h-4 bg-white/10" />
          <div className="flex items-center gap-2">
            <span className="text-[#FFC107] font-mono font-bold text-lg">{stats.pending_count}</span>
            <span className="text-white/40 text-sm">PENDING</span>
          </div>
          <div className="w-px h-4 bg-white/10" />
          <div className="flex items-center gap-2">
            <span className="text-white font-mono font-bold text-lg">{stats.total_count}</span>
            <span className="text-white/40 text-sm">TOTAL</span>
          </div>
        </div>

        {/* Positions list */}
        {positions.length === 0 ? (
          <div className="text-center py-16 bg-[#121212] border border-white/10 rounded-sm">
            <p className="text-white/40 text-lg">No open positions right now.</p>
            <p className="text-white/30 text-sm mt-2">Check back later for updates.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {positions.map((position) => (
              <div
                key={position.id}
                onClick={() => setSelectedPosition(position)}
                className="bg-[#121212] border border-white/10 rounded-sm p-4 md:p-6 cursor-pointer hover:border-white/20 transition-colors group"
                data-testid={`shared-position-${position.id}`}
              >
                {/* Position header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex flex-wrap items-center gap-2">
                    {/* Asset */}
                    <span className="text-xl font-bold text-white">{position.asset}</span>
                    
                    {/* Direction badge */}
                    {position.direction === 'long' ? (
                      <span className="flex items-center gap-1 px-2 py-0.5 bg-[#00E676]/10 border border-[#00E676]/30 rounded text-xs text-[#00E676]">
                        <ArrowUp size={12} weight="bold" />
                        LONG
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 px-2 py-0.5 bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded text-xs text-[#FF3B30]">
                        <ArrowDown size={12} weight="bold" />
                        SHORT
                      </span>
                    )}
                    
                    {/* Trade type badge */}
                    {getTradeTypeBadge(position.trade_type)}
                    
                    {/* Setup badge */}
                    {position.setup_name && (
                      <span className="px-2 py-0.5 bg-white/5 border border-white/10 rounded text-xs text-white/70">
                        {position.setup_name}
                      </span>
                    )}
                    
                    {/* Hedge badge - only show if has_hedge is true */}
                    {position.has_hedge && (
                      <span className="flex items-center gap-1 px-2 py-0.5 bg-blue-500/10 border border-blue-500/30 rounded text-xs text-blue-400">
                        <Shield size={12} weight="fill" />
                        HEDGE
                      </span>
                    )}
                    
                    {/* Partial badge - only show if partial_used is true */}
                    {position.partial_used && (
                      <span className="px-2 py-0.5 bg-orange-500/10 border border-orange-500/30 rounded text-xs text-orange-400">
                        PARTIAL
                      </span>
                    )}
                    
                    {/* Pending indicator */}
                    {position.position_status === 'pending' && (
                      <span className="px-2 py-0.5 bg-[#FFC107]/10 border border-[#FFC107]/30 rounded text-xs text-[#FFC107]">
                        PENDING
                      </span>
                    )}
                  </div>
                  
                  {/* RR + View indicator */}
                  <div className="flex items-center gap-3">
                    {position.rr && (
                      <div className="text-right">
                        <span className="text-white/40 text-xs">RR</span>
                        <div className="text-lg font-mono font-bold text-white">{position.rr.toFixed(2)}</div>
                      </div>
                    )}
                    <CaretRight size={20} className="text-white/20 group-hover:text-white/40 transition-colors" />
                  </div>
                </div>
                
                {/* Position details grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {/* Entry - FIX: Use position.entry not position.entry_price */}
                  <div>
                    <div className="text-white/40 text-xs mb-1">ENTRY</div>
                    <div className="font-mono text-white">{formatPrice(position.entry)}</div>
                  </div>
                  
                  {/* Stop Loss */}
                  <div>
                    <div className="text-white/40 text-xs mb-1">STOP LOSS</div>
                    <div className="font-mono text-[#FF3B30]">{formatPrice(position.stop_loss)}</div>
                  </div>
                  
                  {/* Take Profit */}
                  <div>
                    <div className="text-white/40 text-xs mb-1">TAKE PROFIT</div>
                    <div className="font-mono text-[#00E676]">{formatPrice(position.take_profit)}</div>
                  </div>
                  
                  {/* Timeframe */}
                  <div>
                    <div className="text-white/40 text-xs mb-1">TIMEFRAME</div>
                    <div className="text-white">{position.timeframe || '—'}</div>
                  </div>
                </div>
                
                {/* Footer info */}
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/5">
                  <div className="flex items-center gap-4">
                    {/* Remaining position */}
                    {position.remaining_position_pct !== undefined && position.remaining_position_pct < 100 && (
                      <span className="text-white/40 text-xs">
                        Position: {position.remaining_position_pct}%
                      </span>
                    )}
                  </div>
                  
                  {/* Date */}
                  <span className="text-white/30 text-xs">
                    Opened {formatDate(position.created_at)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Detail Drawer (Read-Only) */}
      {selectedPosition && (
        <div 
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm"
          onClick={() => setSelectedPosition(null)}
        >
          <div 
            className="absolute right-0 top-0 bottom-0 w-full max-w-lg bg-[#0A0A0A] border-l border-white/10 overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Drawer Header */}
            <div className="sticky top-0 z-10 bg-[#0A0A0A] border-b border-white/10 p-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Eye size={16} className="text-white/40" />
                <span className="text-white/40 text-sm">Read Only</span>
              </div>
              <button 
                onClick={() => setSelectedPosition(null)}
                className="p-2 hover:bg-white/10 rounded transition-colors"
              >
                <X size={20} className="text-white/60" />
              </button>
            </div>
            
            {/* Position Identity */}
            <div className="p-6 border-b border-white/5">
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <span className="text-2xl font-bold text-white">{selectedPosition.asset}</span>
                
                {/* Direction */}
                {selectedPosition.direction === 'long' ? (
                  <span className="flex items-center gap-1 px-2 py-0.5 bg-[#00E676]/10 border border-[#00E676]/30 rounded text-xs text-[#00E676]">
                    <ArrowUp size={12} weight="bold" />
                    LONG
                  </span>
                ) : (
                  <span className="flex items-center gap-1 px-2 py-0.5 bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded text-xs text-[#FF3B30]">
                    <ArrowDown size={12} weight="bold" />
                    SHORT
                  </span>
                )}
                
                {/* Trade type */}
                {getTradeTypeBadge(selectedPosition.trade_type)}
              </div>
              
              {/* Additional badges */}
              <div className="flex flex-wrap items-center gap-2">
                {selectedPosition.setup_name && (
                  <span className="px-2 py-0.5 bg-white/5 border border-white/10 rounded text-xs text-white/70">
                    {selectedPosition.setup_name}
                  </span>
                )}
                
                {selectedPosition.has_hedge && (
                  <span className="flex items-center gap-1 px-2 py-0.5 bg-blue-500/10 border border-blue-500/30 rounded text-xs text-blue-400">
                    <Shield size={12} weight="fill" />
                    HEDGE
                  </span>
                )}
                
                {selectedPosition.partial_used && (
                  <span className="px-2 py-0.5 bg-orange-500/10 border border-orange-500/30 rounded text-xs text-orange-400">
                    PARTIAL
                  </span>
                )}
                
                {selectedPosition.position_status === 'pending' && (
                  <span className="px-2 py-0.5 bg-[#FFC107]/10 border border-[#FFC107]/30 rounded text-xs text-[#FFC107]">
                    PENDING
                  </span>
                )}
              </div>
            </div>
            
            {/* Trade Info */}
            <div className="p-6 border-b border-white/5">
              <h3 className="text-white/40 text-xs font-medium mb-4 tracking-wider">TRADE INFO</h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-[#121212] border border-white/10 rounded-sm p-3">
                  <div className="text-white/40 text-xs mb-1">ENTRY</div>
                  <div className="font-mono text-white text-lg">{formatPrice(selectedPosition.entry)}</div>
                </div>
                
                <div className="bg-[#121212] border border-white/10 rounded-sm p-3">
                  <div className="text-white/40 text-xs mb-1">STOP LOSS</div>
                  <div className="font-mono text-[#FF3B30] text-lg">{formatPrice(selectedPosition.stop_loss)}</div>
                  {selectedPosition.initial_stop_loss && selectedPosition.initial_stop_loss !== selectedPosition.stop_loss && (
                    <div className="text-white/30 text-xs font-mono mt-1">
                      Initial: {formatPrice(selectedPosition.initial_stop_loss)}
                    </div>
                  )}
                </div>
                
                <div className="bg-[#121212] border border-white/10 rounded-sm p-3">
                  <div className="text-white/40 text-xs mb-1">TAKE PROFIT</div>
                  <div className="font-mono text-[#00E676] text-lg">{formatPrice(selectedPosition.take_profit)}</div>
                </div>
                
                <div className="bg-[#121212] border border-white/10 rounded-sm p-3">
                  <div className="text-white/40 text-xs mb-1">RISK/REWARD</div>
                  <div className="font-mono text-white text-lg">{selectedPosition.rr?.toFixed(2) || '—'}</div>
                </div>
                
                <div className="bg-[#121212] border border-white/10 rounded-sm p-3">
                  <div className="text-white/40 text-xs mb-1">TIMEFRAME</div>
                  <div className="text-white text-lg">{selectedPosition.timeframe || '—'}</div>
                </div>
                
                <div className="bg-[#121212] border border-white/10 rounded-sm p-3">
                  <div className="text-white/40 text-xs mb-1">ACTIVE SINCE</div>
                  <div className="text-white text-sm">{formatDateTime(selectedPosition.activated_at || selectedPosition.created_at)}</div>
                </div>
                
                {/* Remaining position if partial */}
                {selectedPosition.remaining_position_pct !== undefined && selectedPosition.remaining_position_pct < 100 && (
                  <div className="bg-[#121212] border border-white/10 rounded-sm p-3 col-span-2">
                    <div className="text-white/40 text-xs mb-1">REMAINING POSITION</div>
                    <div className="text-orange-400 text-lg font-mono">{selectedPosition.remaining_position_pct}%</div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Chart/Screenshot - only show if exists */}
            {selectedPosition.screenshot_url && (
              <div className="p-6 border-b border-white/5">
                <h3 className="text-white/40 text-xs font-medium mb-4 tracking-wider flex items-center gap-2">
                  <ChartLine size={14} />
                  CHART
                </h3>
                <div className="rounded-sm overflow-hidden border border-white/10">
                  <img 
                    src={selectedPosition.screenshot_url} 
                    alt="Trade chart" 
                    className="w-full h-auto"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>
              </div>
            )}
            
            {/* Notes - only show if exists and not empty */}
            {selectedPosition.notes && selectedPosition.notes.trim() && (
              <div className="p-6">
                <h3 className="text-white/40 text-xs font-medium mb-4 tracking-wider flex items-center gap-2">
                  <Note size={14} />
                  NOTES
                </h3>
                <div className="bg-[#121212] border border-white/10 rounded-sm p-4">
                  <p className="text-white/80 text-sm whitespace-pre-wrap">{selectedPosition.notes}</p>
                </div>
              </div>
            )}
            
            {/* Bottom padding for mobile */}
            <div className="h-8" />
          </div>
        </div>
      )}

      {/* Conversion CTA */}
      <ConversionCTA />
    </div>
  );
}
